import React from 'react';
import { Droplet, Snowflake, Coffee, Sparkles, BarChart4, Zap, Smartphone, Leaf } from 'lucide-react';
const RestaurantOwnerEssentials = () => {
  const features = [{
    title: 'Premium Flavor Selection',
    description: 'Access to 29+ premium flavors including craft sodas, flavored waters, teas, and cold brews',
    icon: <Coffee className="h-6 w-6 text-blue-500" data-id="element-1189" />
  }, {
    title: 'Alkaline Water System',
    description: 'Unlimited alkaline and sparkling mineral water for drinking and cooking',
    icon: <Droplet className="h-6 w-6 text-blue-500" data-id="element-1190" />
  }, {
    title: 'Ice Machine Integration',
    description: 'Optional integrated ice machine for complete beverage service',
    icon: <Snowflake className="h-6 w-6 text-blue-500" data-id="element-1191" />
  }, {
    title: 'Vitamin-Infused Options',
    description: 'Functional beverages with vitamin enhancements for health-conscious customers',
    icon: <Sparkles className="h-6 w-6 text-blue-500" data-id="element-1192" />
  }, {
    title: 'Usage Analytics',
    description: 'Track consumption patterns to optimize your beverage program',
    icon: <BarChart4 className="h-6 w-6 text-blue-500" data-id="element-1193" />
  }, {
    title: 'Energy Efficient',
    description: 'Low power consumption design reduces operational costs',
    icon: <Zap className="h-6 w-6 text-blue-500" data-id="element-1194" />
  }, {
    title: 'IoT Monitoring',
    description: 'Smart system monitors supply levels and automatically schedules refills',
    icon: <Smartphone className="h-6 w-6 text-blue-500" data-id="element-1195" />
  }, {
    title: 'Eco-Friendly',
    description: 'Eliminates single-use bottles and packaging waste',
    icon: <Leaf className="h-6 w-6 text-blue-500" data-id="element-1196" />
  }];
  return <section className="py-20 bg-gradient-to-b from-white via-blue-50 to-white" data-id="element-1197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1198">
        <div className="text-center mb-12" data-id="element-1199">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-500 text-white shadow-sm mb-4" data-id="element-1200">
            COMPLETE SOLUTION
          </span>
          <h2 className="text-3xl font-bold text-navy-950 sm:text-4xl" data-id="element-1201">
            Restaurant Owner Essentials
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1202">
            Everything you need to transform your beverage program and boost
            profits
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16" data-id="element-1203">
          {/* Left column - Main image with badge */}
          <div className="relative" data-id="element-1204">
            <div className="bg-white p-5 rounded-2xl shadow-xl overflow-hidden transform transition-all hover:scale-[1.02] duration-500" data-id="element-1205">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/98r7X5akSGMg6iSFTjcBfG/WhatsApp_Image_2025-10-14_at_23.54.04.jpg" alt="SmartSoda premium beverage dispenser system" className="rounded-xl w-full h-auto" width="600" height="600" data-id="element-1206" />
              <div className="absolute top-8 right-8 bg-blue-500 text-white px-5 py-2 rounded-full shadow-lg transform rotate-6 hover:rotate-0 transition-transform duration-300" data-id="element-1207">
                <span className="font-bold" data-id="element-1208">VITAMIN INFUSED</span>
              </div>
            </div>
          </div>
          {/* Right column - Feature grid */}
          <div data-id="element-1209">
            <div className="bg-white p-6 rounded-2xl shadow-md" data-id="element-1210">
              <h3 className="text-xl font-bold text-navy-950 mb-6 border-b border-gray-200 pb-3" data-id="element-1211">
                System Highlights
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1212">
                {features.map((feature, index) => <div key={index} className="flex items-start p-4 rounded-xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 hover:shadow-md transition-all duration-300 hover:border-blue-300" data-id="element-1213">
                    <div className="flex-shrink-0 mr-3 mt-1 bg-white p-2 rounded-full shadow-sm" data-id="element-1214">
                      {feature.icon}
                    </div>
                    <div data-id="element-1215">
                      <h4 className="font-semibold text-navy-950" data-id="element-1216">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1" data-id="element-1217">
                        {feature.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
        {/* Bottom section - Implementation process */}
        <div className="bg-white p-6 rounded-2xl shadow-md" data-id="element-1218">
          <h3 className="text-xl font-bold text-navy-950 mb-6 text-center" data-id="element-1219">
            Implementation Process
          </h3>
          <div className="flex flex-col md:flex-row justify-between items-start gap-6" data-id="element-1220">
            <div className="flex-1 text-center p-5 rounded-xl bg-blue-50 hover:shadow-md transition-all duration-300" data-id="element-1221">
              <div className="bg-blue-100 w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" data-id="element-1222">
                <span className="text-blue-700 font-bold text-xl" data-id="element-1223">1</span>
              </div>
              <h4 className="font-semibold text-navy-950 mb-2" data-id="element-1224">
                Site Assessment
              </h4>
              <p className="text-sm text-gray-600" data-id="element-1225">
                Our team evaluates your space and water connections to determine
                optimal placement
              </p>
            </div>
            <div className="flex-1 text-center p-5 rounded-xl bg-blue-50 hover:shadow-md transition-all duration-300" data-id="element-1226">
              <div className="bg-blue-100 w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" data-id="element-1227">
                <span className="text-blue-700 font-bold text-xl" data-id="element-1228">2</span>
              </div>
              <h4 className="font-semibold text-navy-950 mb-2" data-id="element-1229">
                Professional Installation
              </h4>
              <p className="text-sm text-gray-600" data-id="element-1230">
                Expert technicians handle the entire setup with minimal
                disruption to operations
              </p>
            </div>
            <div className="flex-1 text-center p-5 rounded-xl bg-blue-50 hover:shadow-md transition-all duration-300" data-id="element-1231">
              <div className="bg-blue-100 w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" data-id="element-1232">
                <span className="text-blue-700 font-bold text-xl" data-id="element-1233">3</span>
              </div>
              <h4 className="font-semibold text-navy-950 mb-2" data-id="element-1234">
                Staff Training
              </h4>
              <p className="text-sm text-gray-600" data-id="element-1235">
                Comprehensive training on system operation and maintenance for
                your team
              </p>
            </div>
            <div className="flex-1 text-center p-5 rounded-xl bg-blue-50 hover:shadow-md transition-all duration-300" data-id="element-1236">
              <div className="bg-blue-100 w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" data-id="element-1237">
                <span className="text-blue-700 font-bold text-xl" data-id="element-1238">4</span>
              </div>
              <h4 className="font-semibold text-navy-950 mb-2" data-id="element-1239">
                Ongoing Support
              </h4>
              <p className="text-sm text-gray-600" data-id="element-1240">
                24/7 technical support and automatic supply replenishment (IoT
                model only)
              </p>
            </div>
          </div>
        </div>
        <div className="mt-10 text-center" data-id="element-1241">
          <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-1242">
            Schedule Your SmartSoda Installation
          </a>
        </div>
      </div>
    </section>;
};
export default RestaurantOwnerEssentials;